import { isApiError } from '@bookora/ui';
import type { ApiError } from '@bookora/ui';

/**
 * The codes a read-only tenant's writes are refused with (`BillingStateInterceptor`).
 * `FEATURE_NOT_IN_PLAN` is deliberately NOT one of them — see below; a test
 * pins the exclusion.
 */
export const READ_ONLY_CODES: ReadonlySet<string> = new Set([
  'SUBSCRIPTION_INACTIVE',
  'SUBSCRIPTION_PAST_DUE',
  'CLINIC_SUSPENDED',
  'CLINIC_CLOSED',
]);

/**
 * A 402 that means THE CLINIC IS READ-ONLY, not every 402.
 *
 * The other 402s are answered where they happen, and none of them makes the
 * panel read-only; treating them so would put a «renew your subscription»
 * notification over a clinic whose subscription is fine:
 *
 *  · `AI_IMPORT_QUOTA_EXCEEDED`: photo-import pages ran out; the import
 *    screens name the plan with more pages
 *    (`/settings/subscription?plan=<key>&for=ai-import`).
 *  · `PLAN_LIMIT_REACHED`: a seat, patient or branch cap, explained at that cap.
 *  · `FEATURE_NOT_IN_PLAN`: a write the clinic's PLAN does not include (owner
 *    D6 — plan features are enforced server-side on writes; reads never are).
 *    The clinic is not read-only: everything else still saves, and the records
 *    of that feature stay readable. `PlanRefusalWatcher`
 *    (`features/plan/plan-refusal-watcher.tsx`) explains it with the plan that
 *    has the feature and, for the billing holder, «مقایسهٔ پلن‌ها». Counting it
 *    here would tell a clinic whose subscription is fine to «renew».
 */
export function isReadOnlyRefusal(error: unknown): error is ApiError {
  if (!isApiError(error) || error.statusCode !== 402) return false;
  const details = error.details as { readOnly?: unknown } | null | undefined;
  return details?.readOnly === true || READ_ONLY_CODES.has(error.code);
}

/** A closed or suspended clinic cannot be paid back into service; only support reopens it. */
export function isPayableRefusal(code: string): boolean {
  return code !== 'CLINIC_CLOSED' && code !== 'CLINIC_SUSPENDED';
}

/** Why the save was refused, per code, as the first sentence of the notification. */
const WHY: Readonly<Record<string, string>> = {
  SUBSCRIPTION_PAST_DUE: 'صورت‌حساب اشتراک کلینیک پرداخت نشده',
  SUBSCRIPTION_INACTIVE: 'اشتراک کلینیک فعال نیست',
  CLINIC_SUSPENDED: 'حساب کلینیک موقتاً غیرفعال است',
  CLINIC_CLOSED: 'این کلینیک بسته شده',
};

const STILL_READABLE = 'پرونده‌ها رو همچنان می‌بینید، ولی ثبت اطلاعات جدید ممکن نیست.';

/** What the person who hit the refusal can do about it. */
export type RefusalAction = 'pay' | 'tell-owner' | 'support';

export interface RefusalCopy {
  description: string;
  action: RefusalAction;
}

/**
 * The persistent notification's words for a read-only 402: «[why]. [what still
 * works]. [what to do].», and which action it offers.
 *
 * Composed per code rather than by gluing the server's sentence to ours: the
 * server's `reasonFa` is a full paragraph that already ends in a period and
 * already says what still works, so the old concatenation read «….. …» and
 * repeated itself, and it told a receptionist to «renew the subscription» in
 * the same breath as «only the owner can renew it». An unknown read-only code
 * (a new one the panel has not learned yet) keeps the server's own words.
 */
export function readOnlyRefusalCopy(
  code: string,
  serverMessage: string,
  opts: { canBilling: boolean },
): RefusalCopy {
  const payable = isPayableRefusal(code);
  const action: RefusalAction = !payable ? 'support' : opts.canBilling ? 'pay' : 'tell-owner';
  const why = WHY[code];
  if (!why) {
    const own = serverMessage.trim().replace(/[.。]+$/u, '');
    return {
      description:
        action === 'tell-owner'
          ? `${own}. فقط مالک کلینیک می‌تونه اشتراک رو تمدید کنه؛ به مالک کلینیک خبر بدید.`
          : `${own}.`,
      action,
    };
  }
  const next =
    action === 'pay'
      ? 'بعد از تمدید اشتراک، ثبت و ویرایش اطلاعات دوباره فعال می‌شه.'
      : action === 'tell-owner'
        ? 'فقط مالک کلینیک می‌تونه اشتراک رو تمدید کنه؛ به مالک کلینیک خبر بدید.'
        : 'برای رفع اون با پشتیبانی بوکورا تماس بگیرید.';
  return { description: `${why}. ${STILL_READABLE} ${next}`, action };
}
